import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

dayjs.extend(relativeTime)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(advancedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

export const TIMEZONE_PST = 'America/Los_Angeles'

export const IS_OE_PERIOD = dayjs().month() >= 10
export const CURRENT_YEAR = dayjs().year()
export const NEXT_YEAR = dayjs().add(1, 'year').year()

export const DateTimeFormats = {
  a: 'YYYY-MM-DD',
  b: 'YYYY/MM/DD',
  c: 'YYYY-MM-DD HH:mm:ss',
  d: 'MM/DD/YYYY HH:mm:ss',
  e: 'YYYY-MM-DD h:mm A',
  f: 'MM/DD/YYYY h:mm A',
  g: 'YYYY-MM-DD HH:mm',
  h: 'YYYY-MM-DD_HHmm',
  i: 'MMM D, YYYY',
} as const

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const MONTHS_FULL_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export const MONTH_OPTIONS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
]

export const determineAge = (
  dateOfBirth: string,
  parseFormat: string = DateTimeFormats.a
) => {
  const now = dayjs()
  const dob = dayjs(dateOfBirth, parseFormat)
  if (!dob.isValid()) {
    console.error('Invalid Date of Birth provided!')
    return -1
  }

  return now.diff(dob, 'years')
}

export const convertFromISODate = (
  date: string,
  parseFormat: string = DateTimeFormats.a,
  resultFormat: string = DateTimeFormats.d
) => dayjs(date, parseFormat).format(resultFormat)

export const convertToLocalFromUTCISODate = (
  date: string,
  parseFormat: string = DateTimeFormats.c,
  resultFormat: string = DateTimeFormats.f
) => dayjs.utc(date, parseFormat).local().format(resultFormat)

export const convertToText = (
  date: string,
  parseFormat: string = DateTimeFormats.a
) => dayjs(date, parseFormat).format('dddd, MMMM D, YYYY')

/** Jan 1 of the current year — default start of the effective date filter. */
export function getOepFilterStart(): dayjs.Dayjs {
  return dayjs.utc().startOf('year')
}

/** Jan 1 of the next year — default end of the effective date filter. */
export function getOepFilterEnd(): dayjs.Dayjs {
  return dayjs.utc().add(1, 'year').startOf('year')
}

export const getYearOptions = ({ length = 5 }: { length?: number } = {}) => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() // getMonth() returns 0-11 (0=Jan, 10=Nov, 11=Dec)

  // If November (10) or December (11), include next year, otherwise start from current year
  const latestYear = currentMonth >= 10 ? currentYear + 1 : currentYear

  return Array.from({ length }, (_, i) => latestYear - i)
}

export const formatTimestampPST = (
  timestamp: string,
  format = 'MMM DD, YYYY hh:mm A'
) => {
  return dayjs(timestamp).tz(TIMEZONE_PST).format(format)
}

/**
 * Returns the start of the calendar day (00:00:00.000) in PST as a UTC Date.
 * @param d - Any dayjs instance (local, UTC, or tz-aware)
 * @returns UTC Date representing 00:00:00 PST on the same calendar date as `d`
 */
export function startOfDayPST(d: dayjs.Dayjs): Date {
  return dayjs
    .tz(d.format('YYYY-MM-DD'), TIMEZONE_PST)
    .startOf('day')
    .utc()
    .toDate()
}

export function endOfDayPST(d: dayjs.Dayjs): Date {
  return dayjs
    .tz(d.format('YYYY-MM-DD'), TIMEZONE_PST)
    .endOf('day')
    .utc()
    .toDate()
}

export const formatUTCDate = (
  date?: Date | string,
  format?: string
): string => {
  if (!date) return '-'
  return dayjs.utc(date).format(format ?? 'MMM D, YYYY')
}

/**
 * True when `date` is strictly after today, compared by Pacific calendar
 * day. Mirrors EPS's toPstDayjs/computeEnrollmentStatus so the admin UI's
 * Edit Dates gating agrees exactly with the backend's reschedule
 * constraint — a Benefit Effective Date of today or earlier is not future.
 */
export function isFutureDatePST(date: string): boolean {
  const nowPst = dayjs().tz(TIMEZONE_PST)
  const startOfDatePst = dayjs.tz(
    dayjs.utc(date).format('YYYY-MM-DD'),
    TIMEZONE_PST
  )
  return nowPst.isBefore(startOfDatePst)
}

export default dayjs

export function getCurrentYearRange(): { start: string; end: string } {
  return {
    start: dayjs.utc().startOf('year').format(DateTimeFormats.a),
    end: dayjs.utc().endOf('year').format(DateTimeFormats.a),
  }
}
